import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useMultiTenant } from '@/hooks/useMultiTenant';

export interface SystemPayment {
  id: string;
  type: 'receivable' | 'payable';
  payment_date: string;
  payment_value: number;
  interest_value: number;
  fine_value: number;
  discount_value: number;
  fee_value: number;
  /** i.5: payment_value + juros + multa − desconto (valor efetivo de caixa) */
  effective_value: number;
  payment_method: string;
  financial_account_id?: string;
  notes?: string;
  // Dados relacionados
  description?: string;
  customer_name?: string;
  supplier_name?: string;
  transaction_number?: string;
  origin_number?: string;
  reconciled?: boolean;
}

interface UseSystemPaymentsOptions {
  financialAccountId?: string;
  startDate?: string;
  endDate?: string;
  type?: 'receivable' | 'payable';
  unreconciled?: boolean;
  allAccounts?: boolean; // Se true, ignora filtro de conta financeira
  /** R3/R4/R5: pagamentos da conta informada OU sem conta (null) */
  accountOrNull?: string;
}

export const useSystemPayments = (options: UseSystemPaymentsOptions = {}) => {
  const { companyId } = useMultiTenant();
  const { financialAccountId, startDate, endDate, type, unreconciled, allAccounts, accountOrNull } = options;

  const query = useQuery({
    queryKey: ['system-payments', companyId, financialAccountId, startDate, endDate, type, unreconciled, accountOrNull],
    queryFn: async () => {
      if (!companyId) throw new Error('Company ID não disponível');

      const payments: SystemPayment[] = [];

      // Buscar pagamentos de recebíveis (se tipo não especificado ou tipo = receivable)
      if (!type || type === 'receivable') {
        let receivableQuery = supabase
          .from('receivable_payments')
          .select(`
            id,
            payment_date,
            payment_value,
            interest_value,
            fine_value,
            discount_value,
            fee_value,
            payment_method,
            financial_account_id,
            notes,
            receivable:receivable_id!inner (
              description,
              transaction_number,
              origin_number,
              company_id,
              customer:customer_id (
                name
              )
            )
          `)
          .eq('receivable.company_id', companyId)
          .order('payment_date', { ascending: false });

        if (financialAccountId && !allAccounts) {
          receivableQuery = receivableQuery.eq('financial_account_id', financialAccountId);
        }

        if (accountOrNull) {
          receivableQuery = receivableQuery.or(
            `financial_account_id.eq.${accountOrNull},financial_account_id.is.null`
          );
        }

        if (unreconciled) {
          // R1 server-side: pagamento já vinculado nunca é candidato
          receivableQuery = receivableQuery.is('bank_transaction_id', null);
        }

        if (startDate) {
          receivableQuery = receivableQuery.gte('payment_date', startDate);
        }

        if (endDate) {
          receivableQuery = receivableQuery.lte('payment_date', endDate);
        }

        const { data: receivablePayments, error: receivableError } = await receivableQuery;

        if (receivableError) {
          console.error('Erro ao buscar pagamentos de recebíveis:', receivableError);
        } else if (receivablePayments) {
          // Verificar quais já foram conciliados
          let reconciledIds: string[] = [];
          if (unreconciled) {
            const { data: reconciledData } = await supabase
              .from('bank_transactions')
              .select('receivable_payment_id')
              .eq('company_id', companyId)
              .not('receivable_payment_id', 'is', null);
            
            reconciledIds = (reconciledData || []).map(r => r.receivable_payment_id).filter(Boolean) as string[];
          }

          for (const rp of receivablePayments) {
            const isReconciled = reconciledIds.includes(rp.id);
            if (unreconciled && isReconciled) continue;

            const vInterest = Number(rp.interest_value) || 0;
            const vFine = Number(rp.fine_value) || 0;
            const vDiscount = Number(rp.discount_value) || 0;
            const vFee = Number(rp.fee_value) || 0;

            payments.push({
              id: rp.id,
              type: 'receivable',
              payment_date: rp.payment_date,
              payment_value: rp.payment_value,
              interest_value: vInterest,
              fine_value: vFine,
              discount_value: vDiscount,
              fee_value: vFee,
              effective_value: rp.payment_value + vInterest + vFine - vDiscount,
              payment_method: rp.payment_method,
              financial_account_id: rp.financial_account_id || undefined,
              notes: rp.notes || undefined,
              description: (rp.receivable as any)?.description,
              transaction_number: (rp.receivable as any)?.transaction_number,
              origin_number: (rp.receivable as any)?.origin_number ?? undefined,
              customer_name: (rp.receivable as any)?.customer?.name,
              reconciled: isReconciled,
            });
          }
        }
      }

      // Buscar pagamentos de contas a pagar (se tipo não especificado ou tipo = payable)
      if (!type || type === 'payable') {
        let payableQuery = supabase
          .from('payable_payments')
          .select(`
            id,
            payment_date,
            payment_value,
            interest_value,
            fine_value,
            discount_value,
            fee_value,
            payment_method,
            financial_account_id,
            notes,
            payable:payable_id!inner (
              description,
              transaction_number,
              origin_number,
              company_id,
              supplier:supplier_id (
                name
              )
            )
          `)
          .eq('payable.company_id', companyId)
          .order('payment_date', { ascending: false });

        if (financialAccountId && !allAccounts) {
          payableQuery = payableQuery.eq('financial_account_id', financialAccountId);
        }

        if (accountOrNull) {
          payableQuery = payableQuery.or(
            `financial_account_id.eq.${accountOrNull},financial_account_id.is.null`
          );
        }

        if (unreconciled) {
          // R1 server-side: pagamento já vinculado nunca é candidato
          // (F2, D-140C-iii.1 — backfill aplicado, cinto de transição removido)
          payableQuery = payableQuery.is('bank_transaction_id', null);
        }

        if (startDate) {
          payableQuery = payableQuery.gte('payment_date', startDate);
        }

        if (endDate) {
          payableQuery = payableQuery.lte('payment_date', endDate);
        }

        const { data: payablePayments, error: payableError } = await payableQuery;

        if (payableError) {
          console.error('Erro ao buscar pagamentos de contas a pagar:', payableError);
        } else if (payablePayments) {
          for (const pp of payablePayments) {
            const vInterest = Number(pp.interest_value) || 0;
            const vFine = Number(pp.fine_value) || 0;
            const vDiscount = Number(pp.discount_value) || 0;
            const vFee = Number(pp.fee_value) || 0;

            payments.push({
              id: pp.id,
              type: 'payable',
              payment_date: pp.payment_date,
              payment_value: pp.payment_value,
              interest_value: vInterest,
              fine_value: vFine,
              discount_value: vDiscount,
              fee_value: vFee,
              effective_value: pp.payment_value + vInterest + vFine - vDiscount,
              payment_method: pp.payment_method,
              financial_account_id: pp.financial_account_id || undefined,
              notes: pp.notes || undefined,
              description: (pp.payable as any)?.description,
              transaction_number: (pp.payable as any)?.transaction_number,
              origin_number: (pp.payable as any)?.origin_number ?? undefined,
              supplier_name: (pp.payable as any)?.supplier?.name,
              reconciled: false,
            });
          }
        }
      }

      // Ordenar por data
      payments.sort((a, b) => 
        new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime()
      );

      return payments;
    },
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
  });

  const payments = query.data || [];

  return {
    payments,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
